import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';

// NotificationContext — thin polling layer that aggregates all "you have
// something pending" counters. Currently sources:
//   • Store transfers awaiting confirmation at MY warehouse
//   • Shopping cart item count
// Extendable: add more sources in `refresh()`. Consumers read `counters`.
//
// Polling: every 60s, and immediately on mount / login change. Silent on
// error — the UI just shows 0 rather than blowing up.
export const NotificationContext = createContext({
  counters: { inbox_tar_pending: 0, cart_items: 0 },
  total: 0,
  loading: false,
  refresh: () => {},
  refreshCart: () => {},
  markSeen: () => {},
});

const POLL_INTERVAL_MS = 60 * 1000;
const SEEN_STORAGE_KEY = 'notifications:seen_v1';

export const NotificationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [counters, setCounters] = useState({ inbox_tar_pending: 0, cart_items: 0 });
  const [loading, setLoading] = useState(false);
  const [seenAt, setSeenAt] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SEEN_STORAGE_KEY) || '{}'); } catch { return {}; }
  });
  const timerRef = useRef(null);

  const canReceiveTAR = Array.isArray(user?.permissions)
    && (user.permissions.includes('tar.create_from_own') || user.permissions.includes('tar.create_any'));
  // Any user with a reference_warehouse can RECEIVE transfers/replenishments
  // at their warehouse (e.g. técnicos) — backend scopes the inbox safely.
  // Using reference_warehouse (canonical) with legacy fallback
  const userRefWh = user?.reference_warehouse || user?.default_warehouse;
  const canSeeInbox = canReceiveTAR || user?.role === 'admin'
    || !!userRefWh || (user?.allowed_warehouses || []).length > 0;
  
  // Cart access permission
  const canSeeCart = Array.isArray(user?.permissions)
    && (user.permissions.includes('cart.view') || user?.role === 'admin');

  // Refresh cart count independently (for quick updates after add/remove)
  const refreshCart = useCallback(async () => {
    if (!user || !canSeeCart) return;
    try {
      const res = await axios.get(`${API}/shopping-cart`);
      setCounters(prev => ({ ...prev, cart_items: res.data?.total_items || 0 }));
    } catch { /* silent */ }
  }, [user, canSeeCart]);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const next = { inbox_tar_pending: 0, cart_items: 0 };
      // 1. Transfer/replenishment inbox — pending receipts at MY warehouse.
      if (canSeeInbox) {
        try {
          const res = await axios.get(`${API}/store/transfers/inbox`, {
            params: { include_received: false, limit: 1 },
          });
          next.inbox_tar_pending = res.data?.pending_count || 0;
        } catch { /* silent */ }
      }
      // 2. Shopping cart count
      if (canSeeCart) {
        try {
          const res = await axios.get(`${API}/shopping-cart`);
          next.cart_items = res.data?.total_items || 0;
        } catch { /* silent */ }
      }
      setCounters(next);
    } finally {
      setLoading(false);
    }
  }, [user, canSeeInbox, canSeeCart]);

  useEffect(() => {
    if (!user) return undefined;
    refresh();
    timerRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, refresh]);

  // Refresh when user navigates to `/store-transfer` (so badge clears
  // immediately after they act, instead of waiting up to 60s).
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const markSeen = useCallback((key) => {
    const now = new Date().toISOString();
    setSeenAt((prev) => {
      const nextMap = { ...prev, [key]: now };
      try { localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(nextMap)); } catch { /* ignore */ }
      return nextMap;
    });
  }, []);

  const total = Object.values(counters).reduce((s, v) => s + (v || 0), 0);

  const value = { counters, total, loading, refresh, refreshCart, markSeen, seenAt };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
